import { createSelector } from "reselect"
import { get, first, last, indexOf } from "lodash"
import moment from "moment"
import { getStartDateWithBoundaries, getNextModeledDate, isDateValid, getDriftStartAndEndDates } from "helpers/dates"
import { getPeriod, getDate, getViewType } from "modules/portfolio-root/selectors/portfolios"
import { getEarliestDate, getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getBaseStartDate } from "modules/portfolio-root/selectors/base"
import { VIEW_TYPES } from "helpers/base"

const EMPTY_ARRAY = []
const EMPTY_MAP = {}

const getModeledDates = state => get(state, "global.data.modeledDates.data.model.availability.dates", EMPTY_ARRAY)

function computeUnmodeledDates(modeledDates) {
  if (!modeledDates.length) {
    return EMPTY_MAP
  }

  const firstDate = first(modeledDates)
  const lastDate = last(modeledDates)

  let currentDate = firstDate
  const currentMoment = moment(currentDate, "YYYY-MM-DD")

  const result = {}
  while (currentDate <= lastDate) {
    if (currentMoment.isoWeekday() < 6 && indexOf(modeledDates, currentDate) < 0) {
      result[currentDate] = true
    }

    currentMoment.add(1, "day")
    currentDate = currentMoment.format("YYYY-MM-DD")
  }

  return result
}

export const createUnmodeledDatesSelector = (getModeledDates) => createSelector(
  [getModeledDates],
  computeUnmodeledDates,
)

export const getUnmodeledDates = createSelector(
  [getModeledDates],
  (modeledDates) => {
    if (process.env.NODE_ENV !== "production") {
      console.warn("getUnmodeledDates is deprecated. Grab unmodeled dates from the MODEL_METADATA_QUERY query in resources/models.")
    }
    return computeUnmodeledDates(modeledDates)
  },
)

export const getModelStartDate = createSelector(
  [getModeledDates],
  (modeledDates) => first(modeledDates),
)

export const getModelEndDate = createSelector(
  [getModeledDates],
  (modeledDates) => last(modeledDates),
)

export const getEndDate = createSelector(
  [getPeriod, getEarliestDate, getLatestDate],
  (period, earliestDate, latestDate) => {
    if (period?.endDate) {
      if (period.endDate >= earliestDate && period.endDate < latestDate) return period.endDate
      return latestDate
    } else {
      return latestDate || moment().format("YYYY-MM-DD")
    }
  },
)

export const getSelectedDate = createSelector(
  [getDate, getLatestDate, getEarliestDate, getEndDate],
  (selectedDate, latestDate, earliestDate, endDate) => {
    if (!isDateValid(selectedDate)) {
      selectedDate = selectedDate?.adjusted || selectedDate?.selectedDate
    }

    if (!selectedDate) return endDate
    if (selectedDate < earliestDate || selectedDate > latestDate) return latestDate
    return selectedDate
  },
)

export const getStartDate = createSelector(
  [getEndDate, getPeriod, getEarliestDate, getLatestDate],
  getStartDateWithBoundaries,
)

export const getFetchStartDate = createSelector(
  [getStartDate, getBaseStartDate, getViewType],
  (startDate, baseStartDate, viewType) => {
    if (viewType === VIEW_TYPES.ACTIVE && baseStartDate && startDate < baseStartDate) {
      return baseStartDate
    }

    return startDate
  },
)

export const getPerformanceFetchStartDate = createSelector(
  [getStartDate, getBaseStartDate, getViewType, getUnmodeledDates, getLatestDate],
  (startDate, baseStartDate, viewType, unmodeledDates, latestDate) => {
    if (viewType === VIEW_TYPES.ACTIVE && baseStartDate && startDate <= baseStartDate) {
      return getNextModeledDate(baseStartDate, latestDate, unmodeledDates)
    }

    return startDate
  },
)

/**
 * The AI summarizer runs a number of simulations, including performance.
 * To avoid the chance of an invalid simulation when active to a base that has availability after the start date, we always use the performance start date.
 */
export const getAiSummaryStartDate = createSelector(
  [getPerformanceFetchStartDate],
  (performanceFetchStartDate) => performanceFetchStartDate,
)

export const getDriftDates = createSelector(
  [getEndDate, getEarliestDate],
  getDriftStartAndEndDates,
)
